import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { Button } from '../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Users, 
  Share2, 
  MessageSquare,
  FileText,
  Clock,
  Trash2,
  ExternalLink,
  Package,
  User,
  ChevronRight,
  FolderOpen,
  Sparkles
} from 'lucide-react';
import { apiGet, apiDelete, ApiError } from '../lib/apiClient';

// Reusable content component (used by TeamSettings tabs)
export const TeamSharedContent = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sharedAnalyses, setSharedAnalyses] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedShare, setSelectedShare] = useState(null);

  const fetchSharedAnalyses = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiGet('/api/team/shared');
      setSharedAnalyses(data?.shared_analyses || []);
    } catch (err) {
      console.error('Failed to fetch shared analyses:', err);
      const msg = err instanceof ApiError ? err.message : 'Failed to load shared analyses';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSharedAnalyses();
  }, [fetchSharedAnalyses]);

  const handleUnshare = async () => {
    if (!selectedShare) return;
    try {
      await apiDelete(`/api/team/shared/${selectedShare.share_id}`);
      toast.success('Analysis unshared from team');
      setDeleteDialogOpen(false);
      setSelectedShare(null);
      fetchSharedAnalyses();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Failed to unshare analysis';
      toast.error(msg);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const getAnalysisTypeLabel = (type) => {
    switch (type) {
      case 'single': return 'Single Product';
      case 'bulk': return 'Bulk Analysis';
      case 'erp': return 'ERP Import';
      default: return 'Analysis';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (sharedAnalyses.length === 0) {
    return (
      <div className="bg-white rounded-2xl border shadow-sm p-12 text-center">
        <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <FolderOpen className="w-8 h-8 text-slate-400" />
        </div>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">No Shared Analyses Yet</h3>
        <p className="text-slate-500 mb-6 max-w-md mx-auto">
          When team members share their analyses, they'll appear here. You can also share your own analyses from the analysis results page.
        </p>
        <Link to="/history">
          <Button variant="outline" className="gap-2">
            <FileText className="w-4 h-4" />
            View Your Analyses
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-2xl border shadow-sm overflow-hidden">
        <div className="p-4 border-b bg-slate-50">
          <h3 className="font-semibold text-slate-900">
            {sharedAnalyses.length} Shared {sharedAnalyses.length === 1 ? 'Analysis' : 'Analyses'}
          </h3>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/50">
              <TableHead>Analysis</TableHead>
              <TableHead>Shared By</TableHead>
              <TableHead>Date</TableHead>
              <TableHead className="text-center">Comments</TableHead>
              <TableHead className="w-20"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sharedAnalyses.map((item) => (
              <TableRow 
                key={item.share_id}
                className="group hover:bg-slate-50 cursor-pointer"
                onClick={() => navigate(`/analysis/${item.analysis_id}`)}
              >
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-lg flex items-center justify-center">
                      {item.analysis_preview?.type === 'bulk' ? (
                        <Package className="w-5 h-5 text-blue-600" />
                      ) : (
                        <Sparkles className="w-5 h-5 text-indigo-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">
                        {item.analysis_preview?.name || getAnalysisTypeLabel(item.analysis_preview?.type)}
                      </p>
                      <p className="text-sm text-slate-500">
                        {item.analysis_preview?.total_skus || 1} SKU{(item.analysis_preview?.total_skus || 1) > 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 bg-slate-100 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-slate-500" />
                    </div>
                    <span className="text-slate-700">{item.shared_by_name}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2 text-slate-500">
                    <Clock className="w-4 h-4" />
                    <span>{formatDate(item.created_at)}</span>
                  </div>
                </TableCell>
                <TableCell className="text-center">
                  <div className="flex items-center justify-center gap-1">
                    <MessageSquare className="w-4 h-4 text-slate-400" />
                    <span className="font-medium text-slate-700">{item.comment_count || 0}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0"
                      onClick={(e) => { e.stopPropagation(); navigate(`/analysis/${item.analysis_id}`); }}>
                      <ExternalLink className="w-4 h-4 text-slate-500" />
                    </Button>
                    {(item.shared_by === user?.user_id || user?.team_role === 'owner' || user?.team_role === 'admin') && (
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-red-50"
                        onClick={(e) => { e.stopPropagation(); setSelectedShare(item); setDeleteDialogOpen(true); }}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Share2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-blue-800">Sharing with Your Team</p>
            <p className="text-sm text-blue-700 mt-1">
              Share analyses with your team to collaborate on tariff classifications. 
              Team members can view shared analyses and add comments for discussion.
            </p>
          </div>
        </div>
      </div>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Unshare Analysis</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove this analysis from team sharing? 
              Team members will no longer be able to access it.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleUnshare}>Unshare</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Full page wrapper — redirects to Organization page's Shared tab
const TeamShared = ({ user, setUser }) => {
  const navigate = useNavigate();

  useEffect(() => {
    navigate('/organization/shared', { replace: true });
  }, [navigate]);

  return null;
};

export default TeamShared;
